import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  MeetingRoomOutlined as ChangingRoomsIcon,
  SaveOutlined as SaveIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  PageShell,
  AdminSectionPanel,
  ErrorState,
} from "@/components/common";
import { LoadingState } from "@/components/common/LoadingState";
import {
  ChangingRoom,
  ChangingRoomGroup,
  ChangingRoomGroupUpdateData,
  StoreService,
} from "@/services/features/StoreService";

type RoomType = ChangingRoom["type"];
type DraftRoom = ChangingRoom & {
  clientId: string;
  isNew: boolean;
  isModified: boolean;
  originalName: string;
  originalType: RoomType;
};
type DraftGroup = Omit<ChangingRoomGroup, "changingRooms"> & {
  clientId: string;
  isNew: boolean;
  isModified: boolean;
  originalName: string;
  changingRooms: DraftRoom[];
};
type Errors = { areas: Record<string, string>; rooms: Record<string, string> };
type PageError = { message?: string; translationKey?: string };
const roomTypeGroups: ReadonlyArray<{
  labelKey: string;
  types: readonly RoomType[];
}> = [
  {
    labelKey: "management.stores.editChangingRooms.roomTypeGroups.fittingRoom",
    types: ["FITTING_ROOM", "FITTING_ROOM_ANTENNA_ONLY"],
  },
  {
    labelKey: "management.stores.editChangingRooms.roomTypeGroups.pickup",
    types: ["MOBILE_PICKUP", "RUNNER_PICKUP", "CLICK_AND_COLLECT"],
  },
  {
    labelKey: "management.stores.editChangingRooms.roomTypeGroups.other",
    types: ["IN_AISLE", "RFID_CENTRAL"],
  },
];
const emptyErrors = (): Errors => ({ areas: {}, rooms: {} });
const roomTypeKey: Record<RoomType, string> = {
  FITTING_ROOM: "fittingRoom",
  IN_AISLE: "inAisle",
  MOBILE_PICKUP: "mobilePickup",
  RUNNER_PICKUP: "runnerPickup",
  RFID_CENTRAL: "rfidCentral",
  CLICK_AND_COLLECT: "clickAndCollect",
  FITTING_ROOM_ANTENNA_ONLY: "changingRoomAntennaOnly",
};
const getChangeStatus = (entity: { isNew: boolean; isModified: boolean }) =>
  entity.isNew ? "new" : entity.isModified ? "modified" : null;

const EditChangingRooms: React.FC = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const getRoomTypeLabel = (type: RoomType) =>
    t("management.stores.editChangingRooms.roomTypes." + roomTypeKey[type]);
  const { enqueueSnackbar } = useSnackbar();
  const service = useMemo(() => StoreService.getInstance(), []);
  const counter = useRef(0);
  const newId = useCallback((prefix: string) => prefix + "-" + ++counter.current, []);
  const [groups, setGroups] = useState<DraftGroup[]>([]);
  const [errors, setErrors] = useState<Errors>(emptyErrors);
  const [error, setError] = useState<PageError | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const title = t(
    searchParams.get("create") === "true"
      ? "management.stores.editChangingRooms.createTitle"
      : "management.stores.editChangingRooms.title",
  );
  const returnTo =
    typeof (location.state as { returnTo?: unknown } | null)?.returnTo === "string"
      ? (location.state as { returnTo: string }).returnTo
      : "/admin/organization/stores";

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await service.getChangingRoomsByStore(Number(storeId));
      if (!response.success || !response.data) {
        setError({
          translationKey: "management.stores.editChangingRooms.errors.failedLoadChangingRooms",
        });
        return;
      }
      setGroups(
        response.data
          .filter((group) => group.changingRoomGroup && Array.isArray(group.changingRooms))
          .map((group) => ({
            ...group,
            clientId: newId("area"),
            isNew: false,
            isModified: false,
            originalName: group.changingRoomGroup.name,
            changingRooms: group.changingRooms.map((room) => ({
              ...room,
              clientId: newId("room"),
              isNew: false,
              isModified: false,
              originalName: room.name,
              originalType: room.type,
            })),
          })),
      );
      setErrors(emptyErrors());
    } catch (reason) {
      setError(
        reason instanceof Error
          ? { message: reason.message }
          : {
              translationKey:
                "management.stores.editChangingRooms.errors.errorLoadingChangingRooms",
            },
      );
    } finally {
      setLoading(false);
    }
  }, [newId, service, storeId]);

  const errorMessage = error?.message ?? (error?.translationKey ? t(error.translationKey) : null);

  useEffect(() => {
    if (!storeId) {
      navigate("/admin/organization/stores");
      return;
    }
    void load();
  }, [load, navigate, storeId]);

  const updateGroup = (groupId: string, update: (group: DraftGroup) => DraftGroup) =>
    setGroups((current) =>
      current.map((group) => (group.clientId === groupId ? update(group) : group)),
    );
  const updateRoom = (
    groupId: string,
    roomId: string,
    update: Partial<Pick<DraftRoom, "name" | "type">>,
  ) =>
    updateGroup(groupId, (group) => ({
      ...group,
      changingRooms: group.changingRooms.map((room) =>
        room.clientId === roomId
          ? (() => {
              const nextRoom = { ...room, ...update };
              return {
                ...nextRoom,
                isModified:
                  !room.isNew &&
                  (nextRoom.name.trim() !== room.originalName.trim() ||
                    nextRoom.type !== room.originalType),
              };
            })()
          : room,
      ),
    }));
  const validate = () => {
    const next = emptyErrors();
    const areaNames = new Map<string, string>();
    groups.forEach((group) => {
      const area = group.changingRoomGroup.name.trim();
      const normalizedArea = area.toLocaleLowerCase();
      if (!area)
        next.areas[group.clientId] = t(
          "management.stores.editChangingRooms.validation.areaNameRequired",
        );
      else if (areaNames.has(normalizedArea)) {
        const message = t("management.stores.editChangingRooms.validation.areaNameDuplicate");
        next.areas[group.clientId] = message;
        next.areas[areaNames.get(normalizedArea)!] = message;
      } else areaNames.set(normalizedArea, group.clientId);
      const roomNames = new Map<string, string>();
      group.changingRooms.forEach((room) => {
        const name = room.name.trim();
        const normalizedName = name.toLocaleLowerCase();
        if (!name)
          next.rooms[room.clientId] = t(
            "management.stores.editChangingRooms.validation.roomNameRequired",
          );
        else if (roomNames.has(normalizedName)) {
          const message = t("management.stores.editChangingRooms.validation.roomNameDuplicate");
          next.rooms[room.clientId] = message;
          next.rooms[roomNames.get(normalizedName)!] = message;
        } else roomNames.set(normalizedName, room.clientId);
      });
    });
    setErrors(next);
    return !Object.keys(next.areas).length && !Object.keys(next.rooms).length;
  };
  const addRoom = (groupId: string) =>
    updateGroup(groupId, (group) => ({
      ...group,
      changingRooms: [
        ...group.changingRooms,
        {
          clientId: newId("room"),
          isNew: true,
          isModified: false,
          name: t("management.stores.editChangingRooms.defaults.roomName", {
            number: group.changingRooms.length + 1,
          }),
          originalName: t("management.stores.editChangingRooms.defaults.roomName", {
            number: group.changingRooms.length + 1,
          }),
          originalType: "FITTING_ROOM",
          type: "FITTING_ROOM",
        },
      ],
    }));
  const addArea = () => {
    if (!storeId) return;
    setGroups((current) => [
      ...current,
      {
        clientId: newId("area"),
        isNew: true,
        isModified: false,
        originalName: t("management.stores.editChangingRooms.defaults.areaName", {
          number: current.length + 1,
        }),
        name: t("management.stores.editChangingRooms.defaults.areaName", {
          number: current.length + 1,
        }),
        storeId: Number(storeId),
        changingRoomGroup: {
          name: t("management.stores.editChangingRooms.defaults.areaName", {
            number: current.length + 1,
          }),
          storeId: Number(storeId),
        },
        changingRooms: [
          {
            clientId: newId("room"),
            isNew: true,
            isModified: false,
            name: t("management.stores.editChangingRooms.defaults.roomName", { number: 1 }),
            originalName: t("management.stores.editChangingRooms.defaults.roomName", {
              number: 1,
            }),
            originalType: "FITTING_ROOM",
            type: "FITTING_ROOM",
          },
        ],
      },
    ]);
  };
  const submit = async () => {
    if (!storeId) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: ChangingRoomGroupUpdateData[] = groups.map((group) => ({
        name: group.changingRoomGroup.name.trim(),
        id: group.changingRoomGroup.id,
        rooms: group.changingRooms.map((room) => ({
          ...(room.id ? { id: room.id } : {}),
          name: room.name.trim(),
          type: room.type,
        })),
      }));
      const response = await service.updateChangingRoomGroups(Number(storeId), payload);
      if (!response.success) {
        setError({
          translationKey: "management.stores.editChangingRooms.errors.failedUpdateChangingRooms",
        });
        return;
      }
      setConfirming(false);
      enqueueSnackbar(t("management.stores.editChangingRooms.dialogs.successMessage"), {
        variant: "success",
      });
      navigate(returnTo);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? { message: reason.message }
          : {
              translationKey:
                "management.stores.editChangingRooms.errors.errorUpdatingChangingRooms",
            },
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <PageShell breadcrumbs="admin" title={title} icon={<ChangingRoomsIcon />}>
        <Box sx={{ display: "flex", justifyContent: "center", minHeight: "50vh", pt: 8 }}>
          <LoadingState label={t("common.loading")} />
        </Box>
      </PageShell>
    );
  if (error && !groups.length)
    return (
      <PageShell breadcrumbs="admin" title={title} icon={<ChangingRoomsIcon />}>
        <ErrorState
          title={
            errorMessage ?? t("management.stores.editChangingRooms.errors.failedLoadChangingRooms")
          }
          retryLabel={t("common.retry")}
          onRetry={() => void load()}
        />
      </PageShell>
    );
  return (
    <PageShell breadcrumbs="admin" title={title} icon={<ChangingRoomsIcon />} bodySx={{ pb: 3 }}>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Stack spacing={2}>
          {groups.map((group, index) => (
            <AdminSectionPanel
              key={group.clientId}
              title={
                group.changingRoomGroup.name.trim() ||
                t("management.stores.editChangingRooms.sections.areaWithNumber", {
                  number: index + 1,
                })
              }
              icon={<ChangingRoomsIcon fontSize="small" />}
              actions={
                group.isNew && (
                  <Tooltip title={t("management.stores.editChangingRooms.actions.removeArea")}>
                    <IconButton
                      type="button"
                      color="error"
                      aria-label={t("management.stores.editChangingRooms.actions.removeArea")}
                      onClick={() =>
                        setGroups((current) =>
                          current.filter((item) => item.clientId !== group.clientId),
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )
              }
            >
              <TextField
                fullWidth
                required
                label={t("management.stores.editChangingRooms.fields.areaName")}
                value={group.changingRoomGroup.name}
                onChange={(event) =>
                  updateGroup(group.clientId, (current) => ({
                    ...current,
                    isModified:
                      !current.isNew && current.originalName.trim() !== event.target.value.trim(),
                    changingRoomGroup: { ...current.changingRoomGroup, name: event.target.value },
                  }))
                }
                error={Boolean(errors.areas[group.clientId])}
                helperText={errors.areas[group.clientId]}
              />
              <Box sx={{ mt: 3 }}>
                <Typography component="h2" variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                  {t("management.stores.editChangingRooms.sections.changingRooms")}
                </Typography>
                <Stack spacing={1.5}>
                  {group.changingRooms.map((room) => (
                    <Box
                      key={room.clientId}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "minmax(0, 1fr) minmax(220px, 0.55fr) auto",
                        },
                        gap: 1.5,
                        alignItems: "start",
                        py: 0.5,
                      }}
                    >
                      <TextField
                        fullWidth
                        required
                        label={t("management.stores.editChangingRooms.fields.roomName")}
                        value={room.name}
                        onChange={(event) =>
                          updateRoom(group.clientId, room.clientId, { name: event.target.value })
                        }
                        error={Boolean(errors.rooms[room.clientId])}
                        helperText={errors.rooms[room.clientId]}
                      />
                      <FormControl fullWidth required>
                        <InputLabel id={room.clientId + "-type"}>
                          {t("management.stores.editChangingRooms.fields.type")}
                        </InputLabel>
                        <Select
                          labelId={room.clientId + "-type"}
                          label={t("management.stores.editChangingRooms.fields.type")}
                          value={room.type}
                          onChange={(event) =>
                            updateRoom(group.clientId, room.clientId, {
                              type: event.target.value as RoomType,
                            })
                          }
                        >
                          {roomTypeGroups.flatMap((group, index) => [
                            ...(index > 0
                              ? [<Divider key={group.labelKey + "-divider"} component="li" />]
                              : []),
                            <ListSubheader key={group.labelKey} disableSticky component="li">
                              {t(group.labelKey)}
                            </ListSubheader>,
                            ...group.types.map((type) => (
                              <MenuItem key={type} value={type}>
                                {getRoomTypeLabel(type)}
                              </MenuItem>
                            )),
                          ])}
                        </Select>
                      </FormControl>
                      {room.isNew && (
                        <Tooltip
                          title={t("management.stores.editChangingRooms.actions.removeRoom")}
                        >
                          <IconButton
                            type="button"
                            color="error"
                            aria-label={t("management.stores.editChangingRooms.actions.removeRoom")}
                            onClick={() =>
                              updateGroup(group.clientId, (current) => ({
                                ...current,
                                changingRooms: current.changingRooms.filter(
                                  (item) => item.clientId !== room.clientId,
                                ),
                              }))
                            }
                            sx={{ justifySelf: { xs: "end", md: "auto" } }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!room.isNew && (
                        <Box
                          aria-hidden="true"
                          sx={{
                            display: { xs: "none", md: "block" },
                            width: 40,
                            height: 40,
                            justifySelf: { xs: "end", md: "auto" },
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => addRoom(group.clientId)}
                  sx={{ mt: 1.5 }}
                >
                  {t("management.stores.editChangingRooms.actions.addRoom")}
                </Button>
              </Box>
            </AdminSectionPanel>
          ))}
          {groups.length > 0 ? (
            <Stack direction="row" justifyContent="space-between">
              <Button
                type="button"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addArea}
              >
                {t("management.stores.editChangingRooms.actions.addAdditionalArea")}
              </Button>
              <Button
                variant="contained"
                type="button"
                disabled={submitting}
                startIcon={<SaveIcon />}
                onClick={() => {
                  if (validate()) setConfirming(true);
                }}
              >
                {t("management.stores.editChangingRooms.actions.saveChanges")}
              </Button>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={1}>
              <AdminEmptyState
                icon={<ChangingRoomsIcon sx={{ fontSize: 40 }} />}
                text={t("management.stores.editChangingRooms.empty.noAreas")}
                description={t("management.stores.editChangingRooms.empty.addAreaDescription")}
              />
              <Button type="button" variant="contained" startIcon={<AddIcon />} onClick={addArea}>
                {t("management.stores.editChangingRooms.actions.addFittingRoomArea")}
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
      <AdminDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        maxWidth="sm"
        title={t("management.stores.editChangingRooms.dialogs.confirmChangesTitle")}
        icon={<ChangingRoomsIcon />}
        cancelAction={{ label: t("common.cancel"), onClick: () => setConfirming(false) }}
        primaryAction={{
          label: t("management.stores.editChangingRooms.actions.confirm"),
          onClick: () => void submit(),
          loading: submitting,
        }}
      >
        <Typography color="text.secondary">
          {t("management.stores.editChangingRooms.dialogs.confirmConfiguration")}
        </Typography>
        <Stack
          spacing={0}
          sx={{
            mt: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          {groups.map((group) => (
            <Box
              key={group.clientId}
              sx={{
                px: 2,
                py: 1.5,
                "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                  <ChangingRoomsIcon color="primary" fontSize="small" />
                  <Typography fontWeight={700} noWrap>
                    {group.changingRoomGroup.name}
                  </Typography>
                </Stack>
                {getChangeStatus(group) && (
                  <Chip
                    size="small"
                    color={group.isNew ? "primary" : "warning"}
                    variant="outlined"
                    label={t(
                      "management.stores.editChangingRooms.changeStatus." + getChangeStatus(group),
                    )}
                    sx={{
                      borderColor: group.isNew ? "primary.light" : "warning.light",
                      bgcolor: group.isNew ? "rgba(25, 118, 210, 0.08)" : "rgba(237, 108, 2, 0.08)",
                      height: 18,
                      "& .MuiChip-label": {
                        px: 0.65,
                        fontSize: "0.625rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      },
                    }}
                  />
                )}
              </Box>
              {group.changingRooms.length > 0 && (
                <Stack
                  component="ul"
                  spacing={0.75}
                  sx={{
                    mt: 1.25,
                    mb: 0,
                    ps: 2,
                    listStyle: "none",
                    borderInlineStart: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {group.changingRooms.map((room) => (
                    <Box
                      component="li"
                      key={room.clientId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        minWidth: 0,
                        width: "100%",
                        "&::before": {
                          content: '""',
                          width: 12,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        },
                        "&:last-child::after": {
                          content: '""',
                          position: "absolute",
                          insetInlineStart: -16,
                          top: "50%",
                          bottom: -1,
                          width: 2,
                          bgcolor: "background.paper",
                        },
                        position: "relative",
                      }}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: "grey.600",
                          color: "grey.800",
                          bgcolor: "grey.100",
                          fontWeight: 600,
                          height: 20,
                          "& .MuiChip-label": {
                            px: 0.75,
                            fontSize: "0.7rem",
                          },
                        }}
                        label={getRoomTypeLabel(room.type)}
                      />
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                        {room.name}
                      </Typography>
                      {getChangeStatus(room) && (
                        <Chip
                          size="small"
                          color={room.isNew ? "primary" : "warning"}
                          variant="outlined"
                          label={t(
                            "management.stores.editChangingRooms.changeStatus." +
                              getChangeStatus(room),
                          )}
                          sx={{
                            marginInlineStart: "auto",
                            borderColor: room.isNew ? "primary.light" : "warning.light",
                            bgcolor: room.isNew
                              ? "rgba(25, 118, 210, 0.08)"
                              : "rgba(237, 108, 2, 0.08)",
                            height: 18,
                            "& .MuiChip-label": {
                              px: 0.65,
                              fontSize: "0.625rem",
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                            },
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      </AdminDialog>
    </PageShell>
  );
};
export default EditChangingRooms;
